<template>
  <div>
    <aside
      v-if="showOffer"
      class="pwa-install-offer elevation-5"
      :aria-label="$t(&quot;Install Self Biography&quot;)"
    >
      <img
        src="/icons/app-icon.png"
        alt=""
        width="44"
        height="44"
      />
      <div class="pwa-offer-copy">
        <strong>{{ $t("Keep your story close") }}</strong>
        <p>{{ $t("Install Self Biography for easy access from your ") }}{{ mobile ? $t("home screen") : 'desktop' }}.</p>
        <div class="pwa-offer-actions">
          <v-btn
            small
            color="primary"
            @click="openInstallDialog"
          >
            {{ $t("\n            Install app\n          ") }}
          </v-btn>
          <v-btn
            small
            text
            @click="dismissInstallOffer"
          >
            {{ $t("\n            Not now\n          ") }}
          </v-btn>
        </div>
      </div>
    </aside>

    <v-dialog
      v-model="pwa.dialog"
      max-width="480"
      scrollable
      aria-labelledby="pwa-install-title"
    >
      <v-card>
        <v-card-title
          id="pwa-install-title"
          class="pwa-title"
        >
          {{ $t("\n          Install Self Biography\n          ") }}<v-spacer></v-spacer>
          <v-btn
            icon
            :aria-label="$t(&quot;Close installation instructions&quot;)"
            @click="close"
          >
            <v-icon>{{ mdiClose }}</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pwa-install-details">
          <div
            v-if="pwa.firstLoginOffer"
            class="pwa-first-login"
          >
            <img
              src="/icons/app-icon.png"
              alt=""
              width="72"
              height="72"
            />
            <h3>{{ $t("Keep your story close") }}</h3>
            <p>{{ $t("Add Self Biography to your home screen for quick access to your memories, photos, and stories.") }}</p>
          </div>
          <template v-else>
            <p
              v-if="pwa.message"
              role="status"
            >
              {{ pwa.message }}
            </p>
            <template v-if="pwa.nativePrompt && pwa.secure && !pwa.installed">
              <p>{{ $t("Click ") }}<strong>{{ $t("Install now") }}</strong>{{ $t(", then confirm in the window opened by your browser. After installation, open Self Biography from its app icon.") }}</p>
              <v-btn
                color="primary"
                :loading="pwa.busy"
                @click="requestInstall"
              >
                {{ $t("\n                Install now\n              ") }}
              </v-btn>
            </template>
            <template v-else>
              <p v-if="pwa.embedded">
                {{ $t("\n                Open this page in Safari or Chrome.\n              ") }}
              </p>
              <template v-if="pwa.platform === 'ios'">
                <h3>{{ $t("iPhone / iPad") }}</h3>
                <ol>
                  <li>{{ $t("Open Self Biography in ") }}<strong>{{ $t("Safari") }}</strong>{{ $t(". Tap ") }}<strong>{{ $t("Share") }}</strong>{{ $t(", the square with an upward arrow. It may be inside the page menu.") }}</li>
                  <li>{{ $t("Scroll through the sharing options and tap ") }}<strong>{{ $t("Add to Home Screen") }}</strong>.</li>
                  <li>{{ $t("Keep ") }}<strong>{{ $t("Open as Web App") }}</strong>{{ $t(" enabled if shown, then tap ") }}<strong>{{ $t("Add") }}</strong>{{ $t(" to finish.") }}</li>
                  <li>{{ $t("Go to your home screen and tap the ") }}<strong>{{ $t("Self Biography") }}</strong>{{ $t(" icon to open the app.") }}</li>
                </ol>
              </template>
              <template v-else-if="pwa.platform === 'android'">
                <h3>{{ $t("Android") }}</h3>
                <ol>
                  <li>{{ $t("Open Self Biography in ") }}<strong>{{ $t("Chrome") }}</strong>{{ $t(" and tap the three-dot menu ") }}<strong>⋮</strong>{{ $t(" at the top right.") }}</li>
                  <li>{{ $t("Select ") }}<strong>{{ $t("Install app") }}</strong>{{ $t(" or ") }}<strong>{{ $t("Add to Home screen") }}</strong>{{ $t(" from the menu.") }}</li>
                  <li>{{ $t("Confirm by tapping ") }}<strong>{{ $t("Install") }}</strong>{{ $t(" or ") }}<strong>{{ $t("Add") }}</strong>{{ $t(", then open Self Biography from its icon on your home screen or in your app list.") }}</li>
                </ol>
              </template>
              <template v-else-if="pwa.platform === 'mac-safari'">
                <h3>{{ $t("Safari on Mac") }}</h3>
                <ol>
                  <li>{{ $t("With Self Biography open in ") }}<strong>{{ $t("Safari") }}</strong>{{ $t(", choose ") }}<strong>{{ $t("File → Add to Dock") }}</strong>{{ $t(" from the menu bar at the top of the screen.") }}</li>
                  <li>{{ $t("Keep the name ") }}<strong>{{ $t("Self Biography") }}</strong>{{ $t(" and click ") }}<strong>{{ $t("Add") }}</strong>.</li>
                  <li>{{ $t("Click the new Self Biography icon in your ") }}<strong>{{ $t("Dock") }}</strong>{{ $t(" to open the app.") }}</li>
                </ol>
              </template>
              <template v-else>
                <h3>{{ $t("Chrome / Edge") }}</h3>
                <ol>
                  <li>{{ $t("Open Self Biography in ") }}<strong>{{ $t("Chrome") }}</strong>{{ $t(" or ") }}<strong>{{ $t("Edge") }}</strong>{{ $t(". Click the install icon on the right side of the address bar, or find ") }}<strong>{{ $t("Install app") }}</strong>{{ $t(" in the browser menu.") }}</li>
                  <li>{{ $t("Click ") }}<strong>{{ $t("Install") }}</strong>{{ $t(" in the confirmation window.") }}</li>
                  <li>{{ $t("Open Self Biography from the new app shortcut on your computer.") }}</li>
                </ol>
              </template>
            </template>
          </template>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="close"
          >
            {{ pwa.firstLoginOffer ? $t("Not now") : $t("Close") }}
          </v-btn>
          <v-btn
            v-if="pwa.firstLoginOffer"
            color="primary"
            @click="installFromOffer"
          >
            {{ $t("\n            Install app\n          ") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mdiClose } from '@mdi/js'
import {
  pwaState, canOfferInstall, openInstallDialog, dismissInstallOffer, requestInstall,
  markFirstLoginInstallSeen, offerFirstLoginInstall,
} from '@/services/pwa'

export default {
  data: () => ({
    pwa: pwaState, mdiClose, offerReady: false, offerTimer: null, firstLoginTimer: null,
  }),
  computed: {
    mobile() { return ['ios', 'android'].includes(this.pwa.platform) },
    accountId() {
      const { user } = this.$store.state

      return user && user.extra_data && user.extra_data.id ? String(user.extra_data.id) : ''
    },
    canOpenOffer() {
      const { state } = this.$store

      return canOfferInstall() && state.logged && !this.pwa.dialog
        && this.$route.meta.auth
        && !state.agreements && !state.modal_upload && !state.modal_edit && !state.diary && !state.gallery.open
        && !state.customCategories && !state.shareMemoryRequest && !state.edit_pages
    },
    eligible() { return this.canOpenOffer && Date.now() >= this.pwa.dismissedUntil },
    firstLoginAccountId() { return this.mobile && this.canOpenOffer ? this.accountId : '' },
    showOffer() { return !this.mobile && this.offerReady && this.eligible },
  },
  watch: {
    accountId(value) {
      if (this.$store.state.logged && this.pwa.dialog) markFirstLoginInstallSeen(value)
    },
    'pwa.dialog': function dialogChanged(value, previous) {
      if (value && this.$store.state.logged) markFirstLoginInstallSeen(this.accountId)
      if (!value && previous && !this.pwa.installed) dismissInstallOffer()
    },
    '$store.state.logged': function loginChanged(value) {
      if (!value && this.pwa.firstLoginOffer) this.close()
    },
    firstLoginAccountId: {
      immediate: true,
      handler(accountId) {
        clearTimeout(this.firstLoginTimer)
        if (accountId) this.firstLoginTimer = setTimeout(() => this.offerFirstLogin(accountId), 1000)
      },
    },
    eligible: {
      immediate: true,
      handler(value) {
        clearTimeout(this.offerTimer)
        this.offerReady = false
        if (value && !this.mobile) this.offerTimer = setTimeout(() => { this.offerReady = true }, 45000)
      },
    },
  },
  beforeDestroy() { clearTimeout(this.offerTimer); clearTimeout(this.firstLoginTimer) },
  methods: {
    openInstallDialog,
    dismissInstallOffer,
    requestInstall,
    offerFirstLogin(accountId) {
      if (this.firstLoginAccountId !== accountId) return
      if (document.visibilityState === 'hidden' || document.querySelector('.v-dialog__content--active')) {
        this.firstLoginTimer = setTimeout(() => this.offerFirstLogin(accountId), 1000)

        return
      }
      offerFirstLoginInstall(accountId)
    },
    installFromOffer() {
      this.pwa.firstLoginOffer = false
      if (this.pwa.nativePrompt) requestInstall()
    },
    close() { this.pwa.dialog = false; dismissInstallOffer() },
  },
}
</script>

<style scoped lang="scss">
.pwa-install-offer {
  position: fixed;
  z-index: 8;
  bottom: calc(20px + env(safe-area-inset-bottom, 0px));
  left: 20px;
  width: min(380px, calc(100vw - 40px));
  display: flex;
  gap: 12px;
  padding: 16px;
  border-radius: 12px;
  background: var(--v-background-base, #fff);
  color: #42394d;
  border: 1px solid rgba(145, 85, 253, 0.25);
}
.pwa-install-offer img { border-radius: 8px; flex-shrink: 0; }
.pwa-offer-copy { min-width: 0; }
.pwa-offer-copy p { font-size: 13px; line-height: 1.5; margin: 4px 0 12px; }
.pwa-offer-actions { display: flex; flex-wrap: wrap; gap: 8px; }
.pwa-title { flex-wrap: nowrap; font-size: 20px; word-break: normal; }
.pwa-install-details h3 { font-size: 16px; margin-bottom: 12px; }
.pwa-install-details li { margin-bottom: 12px; }
.pwa-install-details .v-btn { max-width: 100%; height: auto; min-height: 36px; white-space: normal; }
.pwa-install-details ::v-deep .v-btn__content { flex: 1 1 auto; }
.pwa-first-login img { display: block; margin-bottom: 20px; padding: 8px; border-radius: 12px; background: #fff; object-fit: contain; }
.pwa-first-login p { margin-bottom: 0; line-height: 1.6; }
</style>
